import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import { useGetTrucksQuery } from '../../../redux/slices/truckSlice';
import usePagination from '../../../hooks/usePagination';
import useSearchFilters from '../../../hooks/useSearchFilters';

// Components
import QuoteCta from '../content/QuoteCta';
import FeaturedTrucks from '../FeaturedTrucks';
import TruckTease from '../TruckTease';
import FeaturedPods from '../FeaturedPods';
import CateringCta from '../content/CateringCta';
import Paginate from '../../shared/Paginate';
import Meta from '../../shared/Meta';
import CateringFaq from '../faqs/CateringFaq';
import SearchFilters from '../search/SearchFilters';

// Styles
import paginateRoundTheme from '../../shared/Paginate-round.module.postcss';
import styles from './CategoryPage.module.postcss';
import SimpleCta from '../content/SimpleCta';

const featuredOptions = { page_size: 3, featured: true };

// Cuisine page URL mapping for cross-links
const CUISINE_PAGES = [
  { name: 'Tacos', url: '/taco-trucks', emoji: '🌮', cateringUrl: '/food-truck-catering/taco-catering' },
  { name: 'BBQ', url: '/bbq-food-trucks', emoji: '🍖', cateringUrl: '/food-truck-catering/bbq-catering' },
  { name: 'Pizza', url: '/pizza-food-trucks', emoji: '🍕', cateringUrl: '/food-truck-catering/pizza-catering' },
  { name: 'Dessert', url: '/dessert-food-trucks', emoji: '🍦', cateringUrl: '/food-truck-catering/dessert-catering' },
  { name: 'Breakfast', url: '/breakfast-food-trucks', emoji: '🥞', cateringUrl: '/food-truck-catering/breakfast-catering' },
  { name: 'Burgers', url: '/burger-food-trucks', emoji: '🍔', cateringUrl: '/food-truck-catering/burger-catering' },
  { name: 'Sandwich', url: '/sandwich-food-trucks', emoji: '🥪' },
  { name: 'Healthy', url: '/healthy-food-trucks', emoji: '🥗' },
  { name: 'Gyros', url: '/gyro-food-trucks', emoji: '🥙' },
  { name: 'Asian', url: '/asian-food-trucks', emoji: '🥡', cateringUrl: '/food-truck-catering/asian-catering' },
];

function CategoryPage({ foodCategory, foodName, seoTitle, seoDescription, seoCanonical, MainText, SecondaryText, Faq, cateringUrl }) {
  const { searchFilters, setFilters } = useSearchFilters({ sendToSearchPage: true });
  const { city_name, vehicle_type, base_url, imageBase } = useContext(ConfigContext);
  const location = useLocation();

  const featuredTrucks = {
    featured: true,
    ...(foodCategory && { food_categories: foodCategory })
  };

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const page = urlParams.get('page') || 1;
    setCurrentPage(page);
  }, [location.search]);

  const { data = {} } = useGetTrucksQuery({
    ...(foodCategory && { food_categories: foodCategory }),
    random_order: true,
    page_size: 9,
    page: currentPage,
  });
  const trucks = data.trucks || [];
  const pagination = data.pagination;

  const { paginationProps, showPagination } = usePagination({
    pagination,
    options: { theme: paginateRoundTheme, previousLabel: '<', nextLabel: '>' }
  });

  const truckType = `${foodName || ''} Food ${vehicle_type}`;

  // Build structured data for this cuisine page
  const structuredData = {
    breadcrumbs: {
      listItems: [
        { '@type': 'ListItem', position: 2, name: 'Food Trucks', item: `${base_url}/explore` },
        { '@type': 'ListItem', position: 3, name: `${foodName} Trucks`, item: `${base_url}${seoCanonical || location.pathname}` },
      ],
    },
    ...(trucks.length > 0 && {
      itemList: {
        name: `Best ${foodName} Food ${vehicle_type}s in ${city_name}`,
        description: seoDescription,
        items: trucks.map((truck) => ({
          name: truck.name,
          url: `${base_url}/food-trucks/${truck.id}`,
          servesCuisine: foodName,
          image: truck.featured_photo ? `${imageBase}/${truck.featured_photo}` : undefined,
        })),
      },
    }),
  };

  // Cross-links: all cuisines except the current one
  const crossLinks = CUISINE_PAGES.filter((c) => c.name !== foodName);

  // Find the matching catering URL for this cuisine
  const matchingCateringUrl = cateringUrl || CUISINE_PAGES.find((c) => c.name === foodName)?.cateringUrl;

  const canonicalUrl = Number(currentPage) > 1 ? `${seoCanonical}?page=${currentPage}` : seoCanonical;

  return (
    <div>
      <Meta
        title={seoTitle || `${truckType}s in ${city_name}`}
        structuredData={structuredData}
        type='website'
        description={seoDescription || `Explore ${city_name}'s best ${truckType.toLowerCase()}s`}
        canonical={canonicalUrl}
      />

      <SearchFilters
        filters={searchFilters}
        setFilters={setFilters}
        noMapToggle
      />

      <div className='Section u-pb0 u-pt0'>
        <nav aria-label='Breadcrumb'>
          <ol className={styles.Breadcrumbs}>
            <li><Link to='/'>Home</Link></li>
            <li><Link to='/explore'>Food {vehicle_type}s</Link></li>
            <li>{foodName} {vehicle_type}s</li>
          </ol>
        </nav>
      </div>

      <div className='Section u-pt05 u-pb0'>
        <QuoteCta />
      </div>

      <FeaturedTrucks
        title='Sponsored Results'
        showMenuLinks
        options={featuredOptions}
      />

      <section className='Section'>
        { MainText }
      </section>

      <FeaturedTrucks noTitle options={featuredTrucks} />

      <section className='Section'>
        <div className={styles.TrucksGrid}>
          { trucks.map((truck) =>
            <TruckTease truck={truck} key={'truck-' + truck.id} />
          )}
        </div>
        { showPagination && <Paginate {...paginationProps} /> }
      </section>

      <section className='Section'>
        <SimpleCta
          text={`Can't find your food ${vehicle_type.toLowerCase()}? Add your business for free!`}
          link='/vendor/register'
          linkTitle='Join Now'
        />
      </section>

      <section className='Section--extraPadding'>
        { SecondaryText }
      </section>

      {/* Catering cross-link */}
      { matchingCateringUrl &&
        <section className='Section'>
          <div className={styles.SimpleCateringCta}>
            <p>Planning an event? Book a {foodName.toLowerCase()} food {vehicle_type.toLowerCase()} for your next gathering.</p>
            <Link to={matchingCateringUrl} className='Button'>
              Book {foodName} Catering
            </Link>
          </div>
        </section>
      }

      <section className='Section'>
        <FeaturedPods />
      </section>

      <section className='Section--extraPadding'>
        { Faq || <CateringFaq /> }
      </section>

      {/* Cross-links to other cuisine pages */}
      <section className='Section'>
        <h3 className='u-textCenter u-phosphate'>Explore More Cuisines</h3>
        <div className={styles.CrossLinks}>
          { crossLinks.map((cuisine) => (
            <Link key={cuisine.url} to={cuisine.url}>
              {cuisine.emoji} {cuisine.name} {vehicle_type}s
            </Link>
          ))}
        </div>
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

CategoryPage.propTypes = {
  foodCategory: PropTypes.string,
  foodName: PropTypes.string,
  seoTitle: PropTypes.string,
  seoDescription: PropTypes.string,
  seoCanonical: PropTypes.string,
  MainText: PropTypes.node.isRequired,
  SecondaryText: PropTypes.node.isRequired,
  Faq: PropTypes.node,
  cateringUrl: PropTypes.string,
};

export default CategoryPage;
